import React, { useRef, useState } from 'react';
import '../../css/codigo-verificacion.css';

/**
 * Campo para códigos numéricos de un solo uso (MFA, verificaciones).
 *
 * Por debajo es UN input real a todo el ancho; las casillas son solo dibujo.
 * Así pegar el código entero, el autocompletado del sistema (one-time-code) y
 * el borrado funcionan de forma nativa, sin saltar el foco entre seis inputs.
 *
 * @param value       dígitos introducidos (string)
 * @param onChange    recibe el nuevo valor, ya limpio de todo lo que no es dígito
 * @param onCompletar se llama con el código cuando se rellena la última casilla
 * @param longitud    número de dígitos (6 por defecto)
 * @param error       marca las casillas en rojo y las sacude al activarse
 * @param compacto    casillas más pequeñas, para formularios densos
 * @param anchoCompleto las casillas se reparten todo el ancho del contenedor
 */
const CodigoVerificacion = ({
  id,
  value = '',
  onChange,
  onCompletar,
  onKeyDown,
  longitud = 6,
  autoFocus = false,
  disabled = false,
  error = false,
  compacto = false,
  anchoCompleto = false,
  ...resto
}) => {
  const inputRef = useRef(null);
  const [enfocado, setEnfocado] = useState(false);

  const cambiar = (e) => {
    const limpio = e.target.value.replace(/\D/g, '').slice(0, longitud);
    onChange?.(limpio);
    if (limpio.length === longitud && value.length !== longitud) onCompletar?.(limpio);
  };

  // Siempre se escribe al final: el cursor no puede quedarse a mitad del código
  const llevarCursorAlFinal = () => {
    const input = inputRef.current;
    if (input) input.setSelectionRange(input.value.length, input.value.length);
  };

  // Mitad del código, para partirlo en dos grupos que se leen de un vistazo
  const corte = longitud >= 6 ? Math.ceil(longitud / 2) : null;
  const activa = Math.min(value.length, longitud - 1);

  const clases = [
    'codigo-verificacion',
    compacto && 'codigo-verificacion--compacto',
    anchoCompleto && 'codigo-verificacion--completo',
    error && 'codigo-verificacion--error',
    disabled && 'codigo-verificacion--deshabilitado',
  ].filter(Boolean).join(' ');

  return (
    <div className={clases} onClick={() => inputRef.current?.focus()}>
      <div className="codigo-verificacion__casillas" aria-hidden="true">
        {Array.from({ length: longitud }, (_, i) => {
          const digito = value[i];
          const esActiva = enfocado && i === activa;
          return (
            <React.Fragment key={i}>
              {corte && i === corte && <span className="codigo-verificacion__separador" />}
              <span
                className={[
                  'codigo-verificacion__casilla',
                  digito && 'codigo-verificacion__casilla--llena',
                  esActiva && 'codigo-verificacion__casilla--activa',
                ].filter(Boolean).join(' ')}
              >
                {digito}
                {esActiva && !digito && <span className="codigo-verificacion__caret" />}
              </span>
            </React.Fragment>
          );
        })}
      </div>
      <input
        {...resto}
        ref={inputRef}
        id={id}
        className="codigo-verificacion__input"
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="one-time-code"
        maxLength={longitud}
        value={value}
        onChange={cambiar}
        onKeyDown={onKeyDown}
        onFocus={() => { setEnfocado(true); llevarCursorAlFinal(); }}
        onBlur={() => setEnfocado(false)}
        onSelect={llevarCursorAlFinal}
        autoFocus={autoFocus}
        disabled={disabled}
        aria-invalid={error || undefined}
        spellCheck={false}
      />
    </div>
  );
};

export default CodigoVerificacion;
